import { apiFetch } from '../../utils/apiFetch';

/**
 * notificationsSlice
 *
 * Manages in-app notifications. Local push notifications are stored in Zustand
 * (and persisted to localStorage). The full notification history is fetched
 * on-demand from the server via fetchNotifications().
 */
export const createNotificationsSlice = (set) => ({
  // ─── Local push notifications (persisted) ───────────────────────────────
  notifications: [],

  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        ...state.notifications,
        {
          id: Date.now(),
          type: 'info',
          duration: 5000,
          ...notification,
        },
      ],
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearNotifications: () => set({ notifications: [] }),

  // ─── Server-side notification history ───────────────────────────────────
  serverNotifications: [],
  serverNotifications_loading: false,
  serverNotifications_error: null,
  serverNotifications_meta: null, // { page, limit, total, total_pages }

  /**
   * Fetch notifications from the server.
   * @param {object} opts
   * @param {number} opts.page   — page number (default 1)
   * @param {number} opts.limit  — items per page (default 20)
   * @param {boolean} opts.append — if true, append to existing list (infinite scroll)
   */
  fetchNotifications: async ({ page = 1, limit = 20, append = false } = {}) => {
    set({ serverNotifications_loading: true, serverNotifications_error: null });
    try {
      const data = await apiFetch(
        `/api/v1/customer/notifications?page=${page}&limit=${limit}`
      );

      // Support varying API response shapes
      const list =
        data?.data?.notifications ??
        data?.data ??
        data?.notifications ??
        [];

      const responseData = data?.data ?? {};
      const responseMeta = responseData.meta ?? data?.meta ?? {};
      const currentPage = Number(responseMeta.page ?? responseData.page ?? page);
      const currentLimit = Number(responseMeta.limit ?? responseData.limit ?? limit);
      const total = Number(responseMeta.total ?? responseData.total ?? list.length);
      const totalPages = Number(
        responseMeta.total_pages ?? responseData.total_pages ?? Math.ceil(total / Math.max(currentLimit, 1)),
      );
      const meta = {
        ...responseMeta,
        page: currentPage,
        limit: currentLimit,
        total,
        total_pages: totalPages,
        has_more: responseMeta.has_more ?? responseData.has_more ?? currentPage < totalPages,
        unread_in_scope: responseMeta.unread_in_scope ?? responseData.unread_in_scope,
      };

      set((state) => ({
        serverNotifications: append
          ? [...state.serverNotifications, ...list]
          : list,
        serverNotifications_loading: false,
        serverNotifications_meta: meta,
      }));
      return list;
    } catch (err) {
      set({
        serverNotifications_loading: false,
        serverNotifications_error: err.message,
      });
      return [];
    }
  },

  /**
   * Mark a notification as read (fire-and-forget).
   * @param {number|string} notificationId
   */
  markNotificationRead: async (notificationId) => {
    try {
      await apiFetch(`/api/v1/customer/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });
      // Update local state optimistically
      set((state) => {
        const wasUnread = state.serverNotifications.some(
          (notification) => notification.id === notificationId && !notification.is_read,
        );
        return {
          serverNotifications: state.serverNotifications.map((n) =>
            n.id === notificationId ? { ...n, is_read: true } : n
          ),
          ...(wasUnread && Number.isFinite(Number(state.serverNotifications_meta?.unread_in_scope))
            ? { serverNotifications_meta: {
                ...state.serverNotifications_meta,
                unread_in_scope: Math.max(0, Number(state.serverNotifications_meta.unread_in_scope) - 1),
              } }
            : {}),
        };
      });
    } catch {
      // Silently ignore — non-critical
    }
  },

  /**
   * Mark all server notifications as read.
   */
  markAllNotificationsRead: async () => {
    try {
      await apiFetch('/api/v1/customer/notifications/read-all', {
        method: 'PATCH',
      });
      set((state) => ({
        serverNotifications: state.serverNotifications.map((n) => ({
          ...n,
          is_read: true,
        })),
        serverNotifications_meta: state.serverNotifications_meta
          ? { ...state.serverNotifications_meta, unread_in_scope: 0 }
          : state.serverNotifications_meta,
      }));
    } catch {
      // Silently ignore
    }
  },
});
